// Tour-derived metadata shared by the React tour page and the build-time prerender.
// Plain CommonJS, Node 12-safe, no dependencies.
const site = require("./site");

/** API rows -> enabled tour attribute objects, in display order. */
function normalizeTours(rows) {
  return (rows || [])
    .map(function (row) {
      return row && row.attributes;
    })
    .filter(function (tour) {
      return tour && tour.enabled === true && tour.pathTitle;
    })
    .sort(function (a, b) {
      return (a.sortOrder || 0) - (b.sortOrder || 0);
    });
}

function tourPath(tour) {
  return "/" + tour.pathTitle;
}

function tourUrl(tour) {
  return site.BASE_URL + tourPath(tour);
}

/** Cloudinary URL for a public id, with or without a leading slash. */
function cld(transform, publicId) {
  const id = String(publicId || "").replace(/^\/+/, "");
  return site.CLOUDINARY + "/" + transform + "/" + id;
}

/** 1200x630 share image: the landscape "top" image when there is one, else the portrait tile. */
function tourImage(tour) {
  const id = tour.topPath || tour.picturePath;
  return id ? cld("c_fill,h_630,w_1200/f_auto,q_auto", id) : site.DEFAULT_OG_IMAGE;
}

/** Wide hero background. Tours without a landscape image get a softly blurred tile instead of a stretched one. */
function tourHeroImage(tour) {
  if (tour.topPath) return cld("c_fill,h_650,w_1800/f_auto,q_auto", tour.topPath);
  if (tour.picturePath) return cld("c_fill,h_650,w_1800/e_blur:400/f_auto,q_auto", tour.picturePath);
  return "";
}

/** Portrait tile at a given width (2x variants for srcset). */
function tourTileImage(tour, width) {
  const w = width || 300;
  const h = Math.round((w * 420) / 300);
  return cld("c_fill,w_" + w + ",h_" + h + "/f_auto,q_auto", tour.picturePath);
}

function tourGroupKeys(tour) {
  return String(tour.groups || "")
    .split(",")
    .map(function (g) {
      return g.trim();
    })
    .filter(Boolean);
}

function tourGroupLabels(tour) {
  return tourGroupKeys(tour).map(function (g) {
    return site.GROUP_LABELS[g] || g;
  });
}

/** <=155 characters, cut on a word boundary, for meta descriptions. */
function shortDescription(tour) {
  const text = String(tour.description || "").replace(/\s+/g, " ").trim();
  if (!text) return "Join " + tour.title + " with American Legacy Tours in Cincinnati. Book your spot today!";
  if (text.length <= 155) return text;
  const cut = text.slice(0, 152);
  const space = cut.lastIndexOf(" ");
  return (space > 80 ? cut.slice(0, space) : cut) + "…";
}

/** ISO date Google accepts for Offer.priceValidUntil (end of the current year). */
function priceValidUntil(now) {
  return (now || new Date()).getFullYear() + "-12-31";
}

function buildTourMeta(tour) {
  return {
    title: tour.title + site.TITLE_SUFFIX,
    description: shortDescription(tour),
    path: tourPath(tour),
    image: tourImage(tour),
    type: "product",
  };
}

function buildTourSchema(tour, now) {
  const url = tourUrl(tour);
  const product = {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": url + "#product",
    name: tour.title,
    description: [tour.description, tour.description2, tour.description3].filter(Boolean).join(" "),
    image: [tourImage(tour)],
    url: url,
    brand: { "@type": "Brand", name: site.SITE_NAME },
    category: tourGroupLabels(tour).join(", "),
  };
  if (tour.price) {
    product.offers = {
      "@type": "Offer",
      url: url,
      price: tour.price,
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      priceValidUntil: priceValidUntil(now),
      seller: { "@id": site.BASE_URL + "/#organization" },
    };
  }
  const breadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: site.BASE_URL + "/" },
      { "@type": "ListItem", position: 2, name: tour.title, item: url },
    ],
  };
  return [product, breadcrumbs];
}

module.exports = {
  normalizeTours: normalizeTours,
  tourPath: tourPath,
  tourUrl: tourUrl,
  cld: cld,
  tourImage: tourImage,
  tourHeroImage: tourHeroImage,
  tourTileImage: tourTileImage,
  tourGroupKeys: tourGroupKeys,
  tourGroupLabels: tourGroupLabels,
  shortDescription: shortDescription,
  priceValidUntil: priceValidUntil,
  buildTourMeta: buildTourMeta,
  buildTourSchema: buildTourSchema,
};
